import { useEffect, useMemo, useRef, useState } from "react";

export const getToken = () => {
  return localStorage.getItem("jwtToken");
};

const stripHtml = (value: unknown) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const fetchPutResponse = (apiEndpoint: string, requestBody: any) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  return fetch(`${apiUrl}/${apiEndpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(requestBody),
  });
};

const fetchGetResponse = (apiEndpoint: string) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  return fetch(`${apiUrl}/${apiEndpoint}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
};

const normalizeSuggestedQuestionsLanguage = (language: string) =>
  (language || "english").toString().trim().toLowerCase();

export const fetchFxSuggestedQuestions = async ({
  instrument,
  language = "english",
}: {
  instrument: string;
  language?: string;
}) => {
  if (!instrument) return null;

  const params = new URLSearchParams({
    instrument,
    language: normalizeSuggestedQuestionsLanguage(language),
  });

  const response = await fetchGetResponse(
    `globallink/insights/fx/suggested_questions?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch FX suggested questions: ${response.status}`);
  }

  const data = await response.json();
  if (!data?.success) {
    throw new Error(
      data?.errorObject?.message || "Failed to fetch FX suggested questions"
    );
  }

  return data.results;
};

export const useFxSuggestedQuestions = ({
  instrument,
  language,
  enabled = true,
}: {
  instrument: string;
  language?: string;
  enabled?: boolean;
}) => {
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [suggestedQuestionsMeta, setSuggestedQuestionsMeta] = useState<any>(null);
  const [suggestedQuestionsLoading, setSuggestedQuestionsLoading] =
    useState(false);
  const [suggestedQuestionsError, setSuggestedQuestionsError] =
    useState<Error | null>(null);

  useEffect(() => {
    let isCurrent = true;

    if (!enabled || !instrument) {
      setSuggestedQuestions([]);
      setSuggestedQuestionsMeta(null);
      setSuggestedQuestionsLoading(false);
      setSuggestedQuestionsError(null);
      return () => {
        isCurrent = false;
      };
    }

    const loadSuggestedQuestions = async () => {
      setSuggestedQuestionsLoading(true);
      setSuggestedQuestionsError(null);

      try {
        const results = await fetchFxSuggestedQuestions({
          instrument,
          language,
        });

        if (!isCurrent) return;

        const questions = Array.isArray(results?.questions)
          ? results.questions
              .map((entry: unknown) => {
                if (typeof entry === "string") return entry.trim();

                if (
                  entry &&
                  typeof entry === "object" &&
                  typeof (entry as { question?: unknown }).question === "string"
                ) {
                  return (entry as { question: string }).question.trim();
                }

                return "";
              })
              .filter(Boolean)
          : [];

        setSuggestedQuestions(questions);
        setSuggestedQuestionsMeta(results);
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error fetching FX suggested questions:", error);
        setSuggestedQuestions([]);
        setSuggestedQuestionsMeta(null);
        setSuggestedQuestionsError(error as Error);
      } finally {
        if (isCurrent) setSuggestedQuestionsLoading(false);
      }
    };

    loadSuggestedQuestions();

    return () => {
      isCurrent = false;
    };
  }, [enabled, instrument, language]);

  return {
    suggestedQuestions,
    suggestedQuestionsMeta,
    suggestedQuestionsLoading,
    suggestedQuestionsError,
  };
};

export const FORWARD_ALLOWED_TENORS = [
  "ON",
  "TN",
  "SN",
  "1D",
  "2D",
  "3D",
  "1W",
  "2W",
  "3W",
  "1M",
  "2M",
  "3M",
  "4M",
  "5M",
  "6M",
  "7M",
  "8M",
  "9M",
  "10M",
  "11M",
  "1Y",
] as const;

const FORWARD_TENOR_ORDER_INDEX = FORWARD_ALLOWED_TENORS.reduce(
  (lookup, tenor, index) => {
    lookup[tenor] = index;
    return lookup;
  },
  {} as Record<string, number>
);

export const normalizeForwardTenor = (tenor: string) =>
  String(tenor || "").trim().toUpperCase();

export const sortForwardTenors = (tenors: string[] = []) => {
  const tenorSet = new Set(tenors.map((tenor) => normalizeForwardTenor(tenor)));

  return FORWARD_ALLOWED_TENORS.filter((tenor) => tenorSet.has(tenor));
};

export const sortForwardPointsByTenor = (forwardPoints: any[] = []) => {
  const forwardPointMap = new Map<string, any>();

  forwardPoints.forEach((point: any) => {
    const normalizedTenor = normalizeForwardTenor(point?.tenor);

    if (
      normalizedTenor &&
      FORWARD_TENOR_ORDER_INDEX[normalizedTenor] !== undefined &&
      !forwardPointMap.has(normalizedTenor)
    ) {
      forwardPointMap.set(normalizedTenor, {
        ...point,
        tenor: normalizedTenor,
      });
    }
  });

  return FORWARD_ALLOWED_TENORS.flatMap((tenor) =>
    forwardPointMap.has(tenor) ? [forwardPointMap.get(tenor)] : []
  );
};

export const fetchWebSocketToken = async () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = getToken();

  if (!apiUrl || !token) {
    throw new Error("Missing API URL or auth token for websocket auth");
  }

  const response = await fetch(`${apiUrl}/ws/v2/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json();
  if (!response.ok || !data?.success || !data?.results?.ws_token) {
    throw new Error(data?.errorObject?.message || "Unable to create websocket token");
  }

  return data.results.ws_token;
};

export const getAuthenticatedWebSocketUrl = async (
  chatId: string,
  userId: string,
  messageId?: string,
  source?: string
) => {
  const apiUrl:any = process.env.REACT_APP_API_URL;
  const wsToken:any = await fetchWebSocketToken();
  const apiHost = new URL(apiUrl).host;
  const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
  const params = new URLSearchParams({
    ws_token: wsToken,
  });

  if (messageId) {
    params.set("message_id", messageId);
  }

  if (source) {
    params.set("source", source);
  }

  return `${wsProtocol}://${apiHost}/ws/v2/${encodeURIComponent(
    chatId
  )}/${encodeURIComponent(userId)}?${params.toString()}`;
};
export const fetchFilteredPrices = async (
  symbol: any,
  setChartData: any,
  setCurrentPrice: any,
  startDate: any = null,
  endDate: any = null,
) => {
  // If startDate and endDate are not provided, set default values
  if (!endDate) {
    endDate = new Date().toISOString(); // current time
  }

  if (!startDate) {
    const oneHourAgo = new Date(new Date().getTime() - 60 * 60 * 1000); // 1 hour prior
    startDate = oneHourAgo.toISOString();
  }

  const requestBody = {
    symbol: symbol,
    start_date: startDate,
    end_date: endDate,
    tenor: "ON",
  };

  try {
    const response = await fetchPutResponse(
      "globallink/prices/filter",
      requestBody
    );

    if (!response.ok) throw new Error("Network response was not ok.");

    const data = await response.json();
    const price_data = data.results.map((x: any) => {
      return {
        ...x,
        current_price: x.spot_midpoint,
      }; // Keep original Date object for sorting}
    });

    setChartData(
      price_data.sort((a: any, b: any) => a.originalDate - b.originalDate)
    );
    setCurrentPrice(price_data[price_data.length-1].current_price);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const fetchInsights = async (
  setInsights: any,
  setTakeaway: any,
  setCurrencyPair: any,
  setCurrentPrice: any,
  setIsloading: any,
  currencyPair: any,
  language: any
) => {
  try {
    const user_language = localStorage.getItem("user_language") ? `&user_language=${localStorage.getItem("user_language")}` : "&user_language=English";
    const response = await fetchGetResponse(
      "globallink/insights/get_insights?currency_pair=" + currencyPair +`${user_language}`
    );

    if (!response.ok) throw new Error("Network response was not ok.");
    const data = await response.json();
    setTakeaway(data.results.takeaway);
    setInsights(data.results.insights);
    setCurrencyPair(data.results.currency_pair);
    setCurrentPrice(data.results.current_price);
    setIsloading(true);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  } finally {
    setTimeout(() => {
      setIsloading(false);
    }, 500);
  }
};

export const fetchInsightsWithTitle = async (
  setInsights: any,
  setCurrentPrice: any,
  currencyPair: any,
  currencyPairRef: any,
  forward_tile :boolean,
  setCountDownInsights: any

) => {
  const forward_data_flag =
  forward_tile ? "&forward_data_flag=true" : "";
  try {
     const user_language = localStorage.getItem("user_language")
      ? `&user_language=${localStorage.getItem("user_language")}`
      : "&user_language=English";
      console.log( `globallink/insights/get_insights?currency_pair=${currencyPair}&title=true&${forward_data_flag}&show_countdown=true${user_language}`)
    const response = await fetchGetResponse(
      `globallink/insights/get_insights?currency_pair=${currencyPair}&title=true&${forward_data_flag}&show_countdown=true${user_language}`
    );

    if (!response.ok) throw new Error("Network response was not ok.");
    const data = await response.json();
    if (currencyPairRef.current === currencyPair) {
      const insights = data.results.insights.map((insight:any) => {
        return {
          ...insight,
          // Raw receipt time; formatted at render in the display time zone.
          timestamp: Date.now(),
        };
      });
      const countDownInsights = insights.filter((insight :any) => insight.type === "count_down");
      const filteredInsights = insights.filter((insight :any) => insight.type !== "count_down");
      setInsights(filteredInsights);
      setCountDownInsights(countDownInsights);
      setCurrentPrice(data.results.current_price);
    }
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const fetchRealtimeSentiment = async ({
  currencyPair,
  forward_tile = false,
  userLanguage,
}: any) => {
  const resolvedLanguage =
    userLanguage || localStorage.getItem("user_language") || "English";
  const queryParts = [
    `currency_pair=${encodeURIComponent(currencyPair)}`,
    "title=true",
    "show_countdown=true",
    `user_language=${encodeURIComponent(resolvedLanguage)}`,
  ];

  if (forward_tile) {
    queryParts.push("forward_data_flag=true");
  }

  const response = await fetchGetResponse(
    `globallink/insights/get_insights?${queryParts.join("&")}`
  );

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const data = await response.json();
  const rawSentiment = data?.results?.sentiment;
  const parsedSentiment =
    rawSentiment == null ? null : Number.parseFloat(String(rawSentiment));
  const normalizedSentiment =
    parsedSentiment != null && Number.isFinite(parsedSentiment)
      ? Math.max(-1, Math.min(1, parsedSentiment))
      : null;

  return {
    currency_pair: data?.results?.currency_pair ?? currencyPair,
    current_price: data?.results?.current_price ?? null,
    sentiment: normalizedSentiment,
  };
};

const clampExecutionScore = (value: unknown) => {
  if (value == null || value === "") return null;

  const parsedValue = Number.parseFloat(String(value));
  if (!Number.isFinite(parsedValue)) return null;

  const normalizedValue =
    parsedValue > 1 && parsedValue <= 100 ? parsedValue / 100 : parsedValue;

  return Math.max(0, Math.min(1, normalizedValue));
};

const clampExecutionConfidence = (value: unknown) => {
  if (value == null || value === "") return null;

  const parsedValue = Number.parseFloat(String(value));
  if (!Number.isFinite(parsedValue)) return null;

  const normalizedValue = parsedValue <= 1 ? parsedValue * 100 : parsedValue;

  return Math.max(0, Math.min(100, Math.round(normalizedValue)));
};

const normalizeExecutionExplanation = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([title, detail]) => [
        String(title || "").trim(),
        stripHtml(detail),
      ])
      .filter(([title, detail]) => title && detail)
  );
};

type FxExecutionDecision = {
  recommended: "algo" | "rfq";
  algo: number | null;
  rfq: number | null;
  confidence: number | null;
};

export const normalizeFxExecutionDecisionMessage = (rawMessage: unknown) => {
  let payload = rawMessage;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return {
        decision: null,
        explanation: {},
      };
    }
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      decision: null,
      explanation: {},
    };
  }

  const rawDecision =
    (payload as any).decision &&
    typeof (payload as any).decision === "object" &&
    !Array.isArray((payload as any).decision)
      ? (payload as any).decision
      : {};

  let algoScore = clampExecutionScore(rawDecision.algo);
  let rfqScore = clampExecutionScore(rawDecision.rfq);

  if (algoScore == null && rfqScore != null) {
    algoScore = Math.max(0, Math.min(1, 1 - rfqScore));
  }
  if (rfqScore == null && algoScore != null) {
    rfqScore = Math.max(0, Math.min(1, 1 - algoScore));
  }

  if (algoScore != null && rfqScore != null) {
    const totalScore = algoScore + rfqScore;
    if (totalScore > 0) {
      algoScore = algoScore / totalScore;
      rfqScore = rfqScore / totalScore;
    }
  }

  let recommended = String(rawDecision.recommended || "")
    .trim()
    .toLowerCase();
  if (recommended !== "algo" && recommended !== "rfq") {
    recommended = (algoScore ?? 0.5) >= (rfqScore ?? 0.5) ? "algo" : "rfq";
  }

  const confidence = clampExecutionConfidence(rawDecision.confidence);

  return {
    decision: {
      recommended: recommended as FxExecutionDecision["recommended"],
      algo: algoScore,
      rfq: rfqScore,
      confidence,
    },
    explanation: normalizeExecutionExplanation((payload as any).explanation),
  };
};

const normalizeUpdateInsightType = (type: unknown) => {
  if (type === "countdown" || type === "count_down") return "count_down";
  return typeof type === "string" && type.trim() ? type : "insight";
};

const clampImpactScore = (value: unknown) => {
  if (value == null || value === "") return null;

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;

  return Math.max(-1, Math.min(1, parsedValue));
};

const parseStreamCurrentPrice = (rawPrice: unknown) => {
  if (rawPrice == null) return null;

  if (typeof rawPrice === "number") {
    return Number.isFinite(rawPrice) ? rawPrice : null;
  }

  if (typeof rawPrice === "object") {
    const nestedValue = Number(
      (rawPrice as any)?.spot_midpoint ?? (rawPrice as any)?.current_price
    );
    return Number.isFinite(nestedValue) ? nestedValue : null;
  }

  if (typeof rawPrice !== "string") return null;

  try {
    const normalized = rawPrice
      .replace(/'/g, '"')
      .replace(/Decimal\("([^"]+)"\)/g, "$1");
    const parsed = JSON.parse(normalized);

    if (typeof parsed === "number") {
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (typeof parsed === "object" && parsed !== null) {
      const nestedValue = Number(
        (parsed as any).spot_midpoint ?? (parsed as any).current_price
      );
      return Number.isFinite(nestedValue) ? nestedValue : null;
    }
  } catch {
    const fallbackValue = Number(rawPrice);
    return Number.isFinite(fallbackValue) ? fallbackValue : null;
  }

  return null;
};

export const useInactivity = (timeout = 1200) => {
  const [isActive, setIsActive] = useState(true);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const resetInactivityTimer = () => {
      setIsActive(true);
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }

      inactivityTimer.current = setTimeout(() => {
        setIsActive(false);
      }, timeout * 1000);
    };

    const handleActivity = () => resetInactivityTimer();

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    resetInactivityTimer();

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [timeout]);

  return isActive;
};

export const useFxExecutionDecision = ({
  currencyPair,
  tradeAmount = "50 million",
  side = "buy_base",
  query = "Based on current market conditions, should I execute this order via Algo or RFQ?",
  language = "English",
  requestSeed = 0,
  enabled = true,
}: {
  currencyPair: string;
  tradeAmount?: string;
  side?: string;
  query?: string;
  language?: string;
  requestSeed?: number;
  enabled?: boolean;
}) => {
  const [decision, setDecision] = useState<FxExecutionDecision | null>(null);
  const [explanation, setExplanation] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusText, setStatusText] = useState("");
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRequestKeyRef = useRef<string | null>(null);

  const requestKey = useMemo(
    () =>
      [
        currencyPair || "",
        tradeAmount || "",
        side || "",
        query || "",
        language || "",
        requestSeed || 0,
      ].join("::"),
    [currencyPair, tradeAmount, side, query, language, requestSeed]
  );

  useEffect(() => {
    completedRequestKeyRef.current = null;
    setDecision(null);
    setExplanation({});
    setLoading(false);
    setError(null);
    setStatusText("");
    setUpdatedAt(null);
  }, [currencyPair, tradeAmount, side, query, language]);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      clearReconnectTimer();
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };

    if (!enabled || !currencyPair) {
      closeSocket();
      return () => {
        closeSocket();
      };
    }

    if (completedRequestKeyRef.current === requestKey) {
      return () => {
        closeSocket();
      };
    }

    let didUnmount = false;
    let hasFinished = false;
    let reconnectEnabled = true;
    let reconnectAttempts = 0;
    let activeAssistantMessageId: string | null = null;
    const maxReconnectAttempts = 5;
    const userId = localStorage.getItem("userId") || "EXECUTION";
    const timeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const randomNumber = Math.floor(100000 + Math.random() * 900000);
    const chatId = `EXECUTIONGPT-${userId}-${currencyPair}-${randomNumber}`;

    const requestBody = {
      chat_id: chatId,
      currency_pair: currencyPair,
      trade_amount: tradeAmount,
      side,
      query,
      userTimeZone: timeZone,
      user_language: language,
      origin: "EXECUTIONGPT",
    };

    const failRequest = (message: string) => {
      if (didUnmount) return;
      setError(stripHtml(message) || "Unable to load Algo vs RFQ decision.");
      setLoading(false);
      setStatusText("");
      reconnectEnabled = false;
    };

    const finishRequest = () => {
      if (didUnmount) return;
      hasFinished = true;
      reconnectEnabled = false;
      setLoading(false);
      clearReconnectTimer();
      activeAssistantMessageId = null;
    };

    const scheduleReconnect = () => {
      if (
        didUnmount ||
        hasFinished ||
        !reconnectEnabled ||
        reconnectAttempts >= maxReconnectAttempts
      ) {
        if (!hasFinished && reconnectAttempts >= maxReconnectAttempts) {
          failRequest(
            "The Algo vs RFQ decision stream disconnected before it could finish."
          );
        }
        return;
      }

      if (!activeAssistantMessageId) {
        failRequest(
          "The Algo vs RFQ decision stream disconnected before it could be resumed."
        );
        return;
      }

      const delayMs = Math.min(1000 * 2 ** reconnectAttempts, 10000);
      reconnectAttempts += 1;
      reconnectTimerRef.current = setTimeout(() => {
        void openSocket(true).catch((socketError) => {
          console.error(
            "FX execution websocket reconnect auth/setup error:",
            socketError
          );
          scheduleReconnect();
        });
      }, delayMs);
    };

    const openSocket = async (isReconnect = false) => {
      closeSocket();

      const wsUrl = await getAuthenticatedWebSocketUrl(
        chatId,
        userId,
        isReconnect ? activeAssistantMessageId || undefined : undefined
      );
      if (didUnmount) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isReconnect) return;
        setLoading(true);
        ws.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        if (didUnmount) return;

        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          console.error("FX execution websocket parse error:", parseError);
          return;
        }

        activeAssistantMessageId =
          data?.message_id ||
          (data?.type === "MESSAGE_ID" && data?.message != null
            ? String(data.message)
            : activeAssistantMessageId);

        const type = data?.type;
        const message = data?.message;

        if (
          type === "FINISH" ||
          type === "DONE" ||
          type === "END" ||
          type === "COMPLETE"
        ) {
          finishRequest();
          try {
            ws.close();
          } catch {}
          return;
        }

        if (type === "ERROR") {
          failRequest(String(message || "Unable to load Algo vs RFQ decision."));
          try {
            ws.close();
          } catch {}
          return;
        }

        if (type === "INFO") {
          setStatusText(stripHtml(message));
          return;
        }

        if (type === "MESSAGE_ID") {
          return;
        }

        if (type !== "FX_EXECUTION_DECISION") {
          return;
        }

        const normalizedPayload = normalizeFxExecutionDecisionMessage(message);
        if (!normalizedPayload.decision) {
          failRequest("Received an invalid Algo vs RFQ decision payload.");
          try {
            ws.close();
          } catch {}
          return;
        }

        completedRequestKeyRef.current = requestKey;
        setDecision(normalizedPayload.decision);
        setExplanation(normalizedPayload.explanation);
        setError(null);
        setStatusText("");
        setUpdatedAt(Date.now());
        setLoading(false);
      };

      ws.onerror = (socketError) => {
        console.error("FX execution websocket error:", socketError);
      };

      ws.onclose = () => {
        if (!didUnmount && !hasFinished && reconnectEnabled) {
          scheduleReconnect();
        }
      };
    };

    void openSocket().catch((socketError) => {
      console.error("FX execution websocket auth/setup error:", socketError);
      failRequest(socketError?.message || "Unable to open execution stream.");
    });

    return () => {
      didUnmount = true;
      closeSocket();
    };
  }, [currencyPair, enabled, language, query, requestKey, side, tradeAmount]);

  return {
    decision,
    explanation,
    loading,
    error,
    statusText,
    updatedAt,
  };
};

export const useInsightsWs = ({
  currencyPair,
  updateSpeed,
  userLanguage,
  inactivityTimeout = 1200,
  showCountDownInsights = true,
  enabled = true,
}: {
  currencyPair: string;
  updateSpeed: number;
  userLanguage?: string;
  inactivityTimeout?: number;
  showCountDownInsights?: boolean;
  enabled?: boolean;
}) => {
  const [insights, setInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [sentiment, setSentiment] = useState<number | null>(null);
  const [sentimentUpdatedAt, setSentimentUpdatedAt] = useState<number | null>(
    null
  );
  const isActive = useInactivity(inactivityTimeout);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedSymbolRef = useRef(currencyPair);
  const pendingInsightsRef = useRef<any[]>([]);

  const resolvedLanguage = userLanguage || "English";

  const chatId = useMemo(() => {
    const symbol = (currencyPair || "").trim();
    return `UPDATES:${symbol}_${resolvedLanguage}`;
  }, [currencyPair, resolvedLanguage]);

  useEffect(() => {
    selectedSymbolRef.current = currencyPair;
  }, [currencyPair]);

  useEffect(() => {
    setLoading(true);
    setInsights([]);
    setSentiment(null);
    setSentimentUpdatedAt(null);
    pendingInsightsRef.current = [];
  }, [currencyPair, resolvedLanguage]);

  useEffect(() => {
    if (!enabled || !isActive || !currencyPair) {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      if (!enabled) {
        setLoading(false);
      }
      return undefined;
    }

    let didUnmount = false;
    const userId = "ticker-update";

    const cleanupReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeWs = () => {
      cleanupReconnectTimer();
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };

    const scheduleReconnect = () => {
      cleanupReconnectTimer();
      const delayMs = Math.max(1000, (updateSpeed || 1) * 1000);
      reconnectTimerRef.current = setTimeout(() => {
        if (!didUnmount && isActive) {
          void connectWs().catch((error) => {
            console.error("Insight stream websocket auth/setup error:", error);
          });
        }
      }, delayMs);
    };

    const normalizeInsights = (items: any[], messageSymbol: string | null) =>
      (Array.isArray(items) ? items : [])
        .filter(Boolean)
        .map((item: any, index: number) => ({
          id:
            item.id ||
            `${Date.now()}-${index}-${String(item.title || "").slice(0, 24)}`,
          title: item.title ?? "",
          description: item.description ?? "",
          type: normalizeUpdateInsightType(item.type),
          currency_pair: messageSymbol || selectedSymbolRef.current,
          // Placeholder; replaced with the feed-release time when shown.
          timestamp: Date.now(),
          received_at: Date.now(),
          start_date: item.start_date || null,
          impact_score: clampImpactScore(item.impact_score),
          question:
            typeof item.question === "string" && item.question.trim()
              ? item.question.trim()
              : null,
        }));

    const connectWs = async () => {
      closeWs();

      const wsUrl = await getAuthenticatedWebSocketUrl(chatId, userId);
      if (didUnmount) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        if (didUnmount) return;

        try {
          const data = JSON.parse(event.data);

          const nextCurrentPrice = parseStreamCurrentPrice(data?.current_price);
          if (nextCurrentPrice != null) {
            setCurrentPrice(nextCurrentPrice);
          }

          const rawSentiment = data?.sentiment;
          const parsedSentiment =
            rawSentiment == null
              ? null
              : Number.parseFloat(String(rawSentiment));
          const normalizedSentiment =
            parsedSentiment != null && Number.isFinite(parsedSentiment)
              ? Math.max(-1, Math.min(1, parsedSentiment))
              : null;

          if (rawSentiment !== undefined) {
            setSentiment(normalizedSentiment);
            setSentimentUpdatedAt(Date.now());
          }

          const messageSymbol =
            data?.currency_pair || data?.symbol || data?.currencyPair || null;

          const normalizedInsights = normalizeInsights(
            data?.insights,
            messageSymbol
          );

          if (normalizedInsights.length > 0) {
            const visibleInsights = normalizedInsights.filter(
              (item) => showCountDownInsights || item.type !== "count_down"
            );
            pendingInsightsRef.current.push(...visibleInsights);

            setLoading(false);
          }
        } catch (error) {
          console.error("[Insights] WS parse error:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("[Insights] WS error:", error);
      };

      ws.onclose = () => {
        if (didUnmount) return;
        if (isActive) scheduleReconnect();
      };
    };

    void connectWs().catch((error) => {
      console.error("Insight stream websocket auth/setup error:", error);
    });

    return () => {
      didUnmount = true;
      closeWs();
    };
  }, [
    chatId,
    currencyPair,
    enabled,
    isActive,
    showCountDownInsights,
    updateSpeed,
  ]);

  useEffect(() => {
    if (!isActive) return undefined;

    const intervalMs = Math.max(250, (updateSpeed || 1) * 1000);

    const processOne = () => {
      if (!isActive) return;

      if (pendingInsightsRef.current.length > 0) {
        const nextInsight = pendingInsightsRef.current.shift();
        if (nextInsight) {
          // A WS batch is dripped out one item per tick, so stamp the moment
          // it enters the feed; `received_at` keeps the raw receipt time.
          const releasedInsight = { ...nextInsight, timestamp: Date.now() };
          setInsights((prev) => [releasedInsight, ...prev]);
          setLoading(false);
        }
      }
    };

    processOne();
    const intervalId = setInterval(processOne, intervalMs);

    return () => clearInterval(intervalId);
  }, [updateSpeed, isActive, showCountDownInsights]);

  return {
    insights,
    loading,
    isActive,
    currentPrice,
    sentiment,
    sentimentUpdatedAt,
  };
};

export const coTraderQuery = async (query: string, currency_pair: string,summary: string) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const requestBody = {
      query: query,
      currency_pair: currency_pair,
      context : summary
    };
    const response = await fetch(`${apiUrl}/globallink/insights/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    const data = await response.json();
    return data.results;
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

export const setSessionVariable = (currency_pair: string) => {
  if (currency_pair) {
    const currencyPairReceived = currency_pair;
    localStorage.setItem("EMALPHA_DASHBAR_CURRENCY_PAIR", currencyPairReceived);
  } else {
    console.log("No Context Available");
  }
};

const PREFERRED_CHART_HEIGHT_KEY = "EMALPHA_PREFERRED_CHART_HEIGHT";
const MIN_CHART_HEIGHT = 150;
const MAX_CHART_HEIGHT = 500;
const DEFAULT_CHART_HEIGHT = 300;

export const clampChartHeight = (height: number) =>
  Math.min(MAX_CHART_HEIGHT, Math.max(MIN_CHART_HEIGHT, height));

export const getPreferredChartHeight = () => {
  const height = Number(localStorage.getItem(PREFERRED_CHART_HEIGHT_KEY));
  return Number.isFinite(height) && height >= MIN_CHART_HEIGHT && height <= MAX_CHART_HEIGHT
    ? height
    : DEFAULT_CHART_HEIGHT;
};

export const savePreferredChartHeight = (height: number) => {
  const nextHeight = clampChartHeight(height);
  localStorage.setItem(PREFERRED_CHART_HEIGHT_KEY, String(nextHeight));
  return nextHeight;
};

export const fetchWidgetInsights = async (
  setInsightsData: any,
  currencyPair: any
) => {
  try {
    const response = await fetchGetResponse(
      "globallink/insights/get_insights?currency_pair=" + currencyPair
    );

    if (!response.ok) throw new Error("Network response was not ok.");
    const data = await response.json();
    setInsightsData({
      insights: [...data.results.insights, ...data.results.takeaway],
      current_price: data.results.current_price,
      currency_pair: currencyPair,
    });
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const fetchCurrencyList = async (setCurrencyList: any) => {
  try {
    const requestBody = {};
    const response = await fetchPutResponse(
      "globallink/prices/get_symbols",
      requestBody
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const data = await response.json();
    setCurrencyList(data.results);
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
  }
};

//  Forward Point Utils

export const fetchForwardPoints = async ({
  setForwardPoints,
  symbol,
  setTenorList,
}: any) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(
      `${apiUrl}/globallink/prices/get_latest_forward_points?symbol=${symbol}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch prices");
    }
    const data = await response.json();
    const forwardPoints = sortForwardPointsByTenor(
      data?.results?.latest_tenor_prices || []
    );
    const tenorList = forwardPoints.map((item: any) => item.tenor);
    setTenorList(tenorList);
    setForwardPoints(forwardPoints);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};
export const fetchForwardPointsForTimeStamp = async ({
  setForwardPoints,
  symbol,
  setTenorList,
  executionTimestamp
}: any) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(
      `${apiUrl}/globallink/prices/get_forward_points?symbol=${symbol}&timestamp=${executionTimestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch prices");
    }
    const data = await response.json();
    const forwardPoints = sortForwardPointsByTenor(
      data?.results?.latest_tenor_prices || []
    );
    const tenorList = forwardPoints.map((item: any) => item.tenor);
    setTenorList(tenorList);
    setForwardPoints(forwardPoints);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};



export const giveFeedback = async ({msgId, feedback,feedbackText,setFeedbackMessageId}:any) => {
  const requestBody = {
    feedback_text: feedbackText,
    message_id: msgId,
    feedback_type: feedback, 
  }

  const response = await fetchPutResponse(`ws/feedback`, requestBody);
  const data = await response.json();
  if (!response.ok) {
    
  } else {
     setFeedbackMessageId((prev:any) =>[...prev,msgId]);
  }
};
